import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../service/auth';

const ALREADY_RETRIED = new HttpContextToken<boolean>(() => false);

function normalizeBase(url: string): string {
  return url.replace(/\/+$/, '');
}

function isSameApiOrigin(url: string, apiBase: string): boolean {
  try {
    const u = new URL(url);
    const base = new URL(apiBase);
    return u.origin === base.origin && u.pathname.startsWith(base.pathname);
  } catch {
    return url.startsWith(apiBase);
  }
}

function pathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    const q = url.indexOf('?');
    return q >= 0 ? url.slice(0, q) : url;
  }
}

/** Login and refresh must not trigger another refresh on 401. */
function isCredentialEndpoint(url: string): boolean {
  const path = pathname(url);
  return (
    path.includes('/auth/refresh') ||
    path.includes('/auth/logout') ||
    path.includes('/auth/csrf') ||
    path.includes('/auth/google') ||
    path.includes('/auth/login') ||
    path.includes('/auth/register') ||
    path.includes('/auth/password/forgot') ||
    path.includes('/auth/password/reset') ||
    path.includes('/auth/providers')
  );
}

function withSession(req: HttpRequest<unknown>, auth: AuthService, alreadyRetried: boolean): HttpRequest<unknown> {
  const method = req.method.toUpperCase();
  const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
  const csrf = auth.getCsrfToken();
  let headers = req.headers;
  if (unsafe && csrf) {
    headers = headers.set('X-CSRF-Token', csrf);
  }
  return req.clone({
    headers,
    withCredentials: true,
    context: req.context.set(ALREADY_RETRIED, alreadyRetried),
  });
}

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBase = normalizeBase(environment.apiUrl);
  if (!isSameApiOrigin(req.url, apiBase)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const outgoing = withSession(req, auth, req.context.get(ALREADY_RETRIED));
  return next(outgoing).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (outgoing.context.get(ALREADY_RETRIED) || isCredentialEndpoint(outgoing.url)) {
        return throwError(() => err);
      }
      return from(auth.refreshSession()).pipe(
        switchMap((ok) => {
          if (!ok) return throwError(() => err);
          return next(withSession(req, auth, true));
        }),
      );
    }),
  );
};
